import React from "react";
import PropTypes from "prop-types";
import { observer } from "mobx-react";
import { Form, Input, Checkbox, Button, Segment, TextArea, Header, Placeholder } from "semantic-ui-react";
import { withTranslation } from "react-i18next";
import CustomField from "~/models/UiMeet3d/GreetingBot/CustomField";
import CustomFieldType from "~/constants/UiMeet3d/GreetingBot/CustomFieldType";
import DnDItem, { DisplayTypes } from "~/components/element/DnDItem";

const customFieldToComponent = ({ customField, onCustomFieldEdit, onCustomFieldRemove, language }) => {
  const actions = (
    <>
      <div className="action">
        <Button icon="pencil" onClick={() => onCustomFieldEdit(customField.id)} />
      </div>
      <div className="action">
        <Button icon="trash" onClick={() => onCustomFieldRemove(customField.id)} />
      </div>
    </>
  );

  if (customField.type === CustomFieldType.PARAGRAPH) {
    return (
      <div className="custom-field-wrapper">
        <div className="field">
          <p>{customField[`label_${language}`]}</p>
        </div>
        {actions}
      </div>
    );
  }

  const FieldWrapper = control => (
    <Form.Field required={customField.mandatory}>
      <label>{customField[`label_${language}`]}</label>
      <div className="custom-field-wrapper">
        <div className="field">{control}</div>
        {actions}
      </div>
    </Form.Field>
  );

  if (customField.type === CustomFieldType.INPUT_TEXT) {
    return FieldWrapper(<Input readOnly fluid defaultValue="" />);
  }

  if (customField.type === CustomFieldType.INPUT_EMAIL) {
    return FieldWrapper(<Input readOnly fluid defaultValue="" placeholder="john@example.com" />);
  }

  if (customField.type === CustomFieldType.TEXTAREA) {
    return FieldWrapper(<TextArea readOnly defaultValue={customField[`label_${language}`]} />);
  }

  if (customField.type === CustomFieldType.CHECKBOX) {
    return FieldWrapper(<Checkbox readOnly defaultChecked={false} defaultValue={false} />);
  }

  throw Error(`Unsupported customField type: ${customField.type}`);
};

const GreetingBotFormPreview = observer(
  ({ t, i18n, model, customFields, onCustomFieldEdit, onCustomFieldRemove, onCustomFieldHover }) => {
    return (
      <Form className="form-preview greeting-bot-form-wrap">
        <h4>{t("preview")}</h4>
        <Header as="h2" attached="top" color="teal">
          {model[`action_form_title_${i18n.language}`] || t("clientPanelMisc:titleGoesHere")}
        </Header>
        <Segment attached>
          {customFields.length === 0 && (
            <>
              <p className="help">
                {t("clientPanelMisc:addCustomFields")} {t("clientPanelMisc:theyWillShowUpHere")}
              </p>
              <Placeholder>
                <Placeholder.Line />
                <Placeholder.Line />
                <Placeholder.Line />
              </Placeholder>
            </>
          )}

          <ul>
            {customFields.map(customField => (
              <DnDItem
                key={customField.id}
                as="li"
                displayType={DisplayTypes.LIST}
                className="custom-field-container"
                item={{
                  id: customField.id,
                  type: "customField",
                  accept: ["customField"],
                }}
                onHover={onCustomFieldHover}
              >
                {customFieldToComponent({
                  customField,
                  onCustomFieldEdit,
                  onCustomFieldRemove,
                  language: i18n.language,
                })}
              </DnDItem>
            ))}
          </ul>
          <div className="custom-form-buttons">
            <Button primary>{t("greetingBotDefaultValues:actionFormButtonYes")}</Button>
            <Button secondary>{t("greetingBotDefaultValues:actionFormButtonNo")}</Button>
          </div>
        </Segment>
      </Form>
    );
  },
);

GreetingBotFormPreview.propTypes = {
  t: PropTypes.func.isRequired,
  i18n: PropTypes.object.isRequired,
  customFields: PropTypes.arrayOf(PropTypes.instanceOf(CustomField)).isRequired,
  onCustomFieldEdit: PropTypes.func.isRequired,
  onCustomFieldRemove: PropTypes.func.isRequired,
  onCustomFieldHover: PropTypes.func.isRequired,
  model: PropTypes.shape({
    action_form_title_en: PropTypes.string,
    action_form_title_fr: PropTypes.string,
  }),
};

export default withTranslation()(GreetingBotFormPreview);
